/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useState, useMemo, useEffect } from "react";
import { FaTimes, FaChevronLeft, FaChevronRight, FaSearchPlus, FaPlay } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Footer from "../minicomponents/Footer";

/**
 * Processes the webpack require.context module to group images by their
 * subfolder directory.
 * @returns {Object} An object where keys are category names (folder names)
 * and values are arrays of image objects ({ id, src }).
 */
function organizeImagesByFolder() {
  // Use require.context to dynamically import all images from subfolders.
  // The 'true' argument tells webpack to search in subdirectories.
  const imageFiles = require.context(
    "../../imgs/gallery/",
    true,
    /\.(png|jpe?g|svg)$/
  );

  // Use reduce to group images into an object by folder name
  return imageFiles.keys().reduce((acc, path) => {
    // The path looks like './Landscapes/image1.jpg'
    const pathParts = path.split("/");

    // We only care about files in a subfolder, so we expect at least 3 parts: '.', 'folder', 'file.jpg'
    if (pathParts.length < 3) {
      return acc;
    }

    const category = pathParts[1]; // e.g., 'Landscapes'

    // Initialize the category array if it doesn't exist
    if (!acc[category]) {
      acc[category] = [];
    }

    // Add the image object to its category
    acc[category].push({
      id: path, // Use the full path as a unique key
      src: imageFiles(path),
    });

    return acc;
  }, {});
}

// YouTube Video Gallery data from @livethewill7257
const videoGallery = [
  {
    id: "vid-1",
    videoId: "MtpmWJ5Bh4M",
    title: "Groot Nieuws Radio Follow The Fire - LiveTheWill",
    type: "Video",
  },
  {
    id: "vid-2",
    videoId: "dCosGfv1O-E",
    title: "Geen angst - LiveTheWill",
    type: "Video",
  },
  {
    id: "vid-3",
    videoId: "dUtlBHKDqtk",
    title: "Heer U kent mij als geen ander - Cover by LiveTheWill",
    type: "Video",
  },
  {
    id: "vid-4",
    videoId: "UDX_UK8bI2I",
    title: "Album opnames! Stay tuned voor onze eerste single!",
    type: "Video",
  },
  {
    id: "vid-5",
    videoId: "OItuYpLggng",
    title: "Wake Up by LiveTheWill - Preview",
    type: "Video",
  },
  {
    id: "vid-6",
    videoId: "KCTncz79Ghk",
    title: "Nog een paar dagen en dan is ons eerste EP uit! #followthefire",
    type: "Short",
  },
  {
    id: "vid-7",
    videoId: "-jIP5B3w4Wc",
    title: "Preview van ons nieuwe nummer 'Follow the fire!'",
    type: "Short",
  },
  {
    id: "vid-8",
    videoId: "jyEW9pkHO1c",
    title: "Daniël over ons nieuwe nummer 'Wake up!'",
    type: "Short",
  },
  {
    id: "vid-9",
    videoId: "IuT1ro--43Y",
    title: "Preview of our new song 'Wake up!'",
    type: "Short",
  },
  {
    id: "vid-10",
    videoId: "BDqHbZQ53Qc",
    title: "Paulus over ons nieuwe nummer 'Prayers'",
    type: "Short",
  },
  {
    id: "vid-11",
    videoId: "sjTY-TlcBdQ",
    title: "Daan over het nieuwe nummer 'Home!'",
    type: "Short",
  },
  {
    id: "vid-12",
    videoId: "nQ2kzex5cFI",
    title: "Preview of our new song 'Home'",
    type: "Short",
  },
  {
    id: "vid-13",
    videoId: "d9u1kQiIhdU",
    title: "Voorproefje van 'Prayers'",
    type: "Short",
  },
  {
    id: "vid-14",
    videoId: "RLRPDQcsPTI",
    title: "Voorproefje What I Fear!",
    type: "Short",
  },
  {
    id: "vid-15",
    videoId: "wMOfGvMFKJE",
    title: "Ons eerste EP! Follow the fire komt uit op 1 juni 2026!",
    type: "Short",
  },
  {
    id: "vid-16",
    videoId: "QAS4NAIxpxc",
    title: "Album opnames! Stay tuned voor onze eerste single!",
    type: "Short",
  },
  {
    id: "vid-17",
    videoId: "X_p8U6q8qik",
    title: "'Prayers' by LiveTheWill - Preview!",
    type: "Short",
  },
  {
    id: "vid-18",
    videoId: "xmuOWV8csPU",
    title: "'Home' by LiveTheWill - Preview!",
    type: "Short",
  }
];

function GalleryPage() {
  const { t } = useTranslation();

  // useMemo ensures that the image organization logic runs only once.
  const galleryCategories = useMemo(() => {
    const organized = organizeImagesByFolder();
    return Object.entries(organized).map(([name, images]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1), // Capitalize the category name
      images,
    }));
  }, []);

  const [activeTab, setActiveTab] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});

  const handleImageLoad = (id) => {
    setLoadedImages((prev) => ({ ...prev, [id]: true }));
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Compute categories list
  const categoriesList = useMemo(() => {
    return ["All", "Videos", ...galleryCategories.map((c) => c.name)];
  }, [galleryCategories]);

  // Compute filtered media items (combines photos and videos)
  const activeMediaList = useMemo(() => {
    const allPhotos = galleryCategories.flatMap((category) =>
      category.images.map((img) => ({
        ...img,
        category: category.name,
        isVideo: false,
      }))
    );

    const allVideos = videoGallery.map((vid) => ({
      id: vid.id,
      src: `https://img.youtube.com/vi/${vid.videoId}/hqdefault.jpg`,
      videoId: vid.videoId,
      category: "Videos",
      title: vid.title,
      isVideo: true,
      type: vid.type,
    }));

    if (activeTab === "All") {
      return [...allPhotos, ...allVideos];
    }
    if (activeTab === "Videos") {
      return allVideos;
    }

    const found = galleryCategories.find((c) => c.name === activeTab);
    return found
      ? found.images.map((img) => ({
          ...img,
          category: found.name,
          isVideo: false,
        }))
      : [];
  }, [activeTab, galleryCategories]);

  const handleOpenLightbox = (index) => {
    setLightboxIndex(index);
  };

  const handleCloseLightbox = () => {
    setLightboxIndex(null);
  };

  const handlePrevImage = (e) => {
    if (e) e.stopPropagation();
    setLightboxIndex((prev) =>
      prev === 0 ? activeMediaList.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e) => {
    if (e) e.stopPropagation();
    setLightboxIndex((prev) =>
      prev === activeMediaList.length - 1 ? 0 : prev + 1
    );
  };

  // Keyboard navigation for Lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleCloseLightbox();
      if (e.key === "ArrowLeft") handlePrevImage();
      if (e.key === "ArrowRight") handleNextImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex, activeMediaList]);

  return (
    <div className="bg-gray-900 text-white px-6 py-12 md:px-10 md:py-16 pt-24 md:pt-28 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <header className="text-center mb-12">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 text-white alt-font"
            style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.15)" }}
          >
            {t("gallery.title", "Gallery")}
          </h1>
          <p className="text-lg text-gray-400 max-w-xl mx-auto">
            {t("gallery.description", "Take a look at our live shows, studio sessions, and releases.")}
          </p>
        </header>

        {galleryCategories.length > 0 || videoGallery.length > 0 ? (
          <>
            {/* Category Filter Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {categoriesList.map((categoryName) => (
                <button
                  key={categoryName}
                  onClick={() => {
                    setActiveTab(categoryName);
                    setLightboxIndex(null);
                  }}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 transform active:scale-95 border ${
                    activeTab === categoryName
                      ? "bg-teal-500 text-white border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.3)]"
                      : "bg-gray-800 text-slate-400 border-gray-700/50 hover:text-white hover:bg-gray-700/80"
                  }`}
                >
                  {categoryName === "Videos" ? t("menuVideos", "Video's") : categoryName}
                </button>
              ))}
            </div>

            {/* Media Grid */}
            {activeMediaList.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 lg:gap-8">
                {activeMediaList.map((item, index) => (
                  <div
                    key={item.id}
                    onClick={() => handleOpenLightbox(index)}
                    className="group relative aspect-square bg-gray-800 border border-gray-700/50 rounded-2xl overflow-hidden shadow-lg hover:shadow-[0_8px_30px_rgb(0,0,0,0.3)] cursor-pointer transform hover:scale-[1.02] transition-all duration-300 ease-out"
                  >
                    {/* Shimmer/Skeleton Placeholder */}
                    {!loadedImages[item.id] && (
                      <div className="absolute inset-0 bg-gray-850 animate-pulse flex items-center justify-center">
                        <div className="w-8 h-8 border-3 border-teal-500/20 border-t-teal-500 rounded-full animate-spin"></div>
                      </div>
                    )}

                    <img
                      src={item.src}
                      alt={item.title || `${item.category || "Gallery"} image`}
                      className={`w-full h-full object-cover transform group-hover:scale-105 transition-all duration-500 ease-out ${
                        loadedImages[item.id] ? "opacity-100 scale-100" : "opacity-0 scale-95"
                      }`}
                      loading="lazy"
                      onLoad={() => handleImageLoad(item.id)}
                    />

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-slate-950/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-between p-6">
                      <div className="flex justify-end">
                        <span className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white text-base">
                          {item.isVideo ? <FaPlay className="ml-0.5 text-sm" /> : <FaSearchPlus />}
                        </span>
                      </div>
                      <div>
                        <span className="text-teal-400 text-xs font-mono tracking-widest uppercase mb-1 block">
                          {item.category}
                        </span>
                        <h4 className="text-white text-base font-bold">
                          {item.isVideo ? t("releases.listen", "Watch Video") : t("gallery.viewImage", "View Image")}
                        </h4>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-xl text-gray-400 mt-10">
                {t("gallery.noImages", "No items available in this category.")}
              </p>
            )}
          </>
        ) : (
          <p className="text-center text-xl text-gray-400 mt-10">
            {t("gallery.empty", "Our gallery is currently empty. Please check back soon!")}
          </p>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 select-none"
          onClick={handleCloseLightbox}
        >
          {/* Close Button */}
          <button
            onClick={handleCloseLightbox}
            className="absolute top-6 right-6 z-[10000] w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center text-xl transition-all border border-white/10"
            aria-label="Close lightbox"
          >
            <FaTimes />
          </button>

          {/* Prev Button */}
          <button
            onClick={handlePrevImage}
            className="absolute left-4 sm:left-8 z-[10000] w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center text-xl transition-all border border-white/10"
            aria-label="Previous image"
          >
            <FaChevronLeft />
          </button>

          {/* Media Display */}
          <div className="relative max-w-full max-h-[80vh] flex items-center justify-center">
            {activeMediaList[lightboxIndex].isVideo ? (
              <div 
                className="bg-black rounded-2xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200"
                style={{
                  aspectRatio: activeMediaList[lightboxIndex].type === 'Short' ? '9/16' : '16/9',
                  width: activeMediaList[lightboxIndex].type === 'Short' ? 'min(85vw, 360px)' : 'min(85vw, 800px)',
                  maxHeight: '75vh',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${activeMediaList[lightboxIndex].videoId}?autoplay=1`}
                  title={activeMediaList[lightboxIndex].title || "YouTube video player"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="border-0 block"
                ></iframe>
              </div>
            ) : (
              <img
                src={activeMediaList[lightboxIndex].src}
                alt="Expanded view"
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            
            {/* Info Overlay */}
            <div
              className="absolute bottom-4 left-4 right-4 bg-slate-950/80 backdrop-blur-sm border border-white/10 p-4 rounded-xl text-left"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-teal-400 text-xs font-mono uppercase tracking-widest mb-0.5">
                {activeMediaList[lightboxIndex].category}
              </p>
              <h3 className="text-white text-sm font-semibold">
                {activeMediaList[lightboxIndex].isVideo
                  ? activeMediaList[lightboxIndex].title
                  : t("gallery.photoOf", {
                      current: lightboxIndex + 1,
                      total: activeMediaList.filter(item => !item.isVideo).length, // Current photo count index is handled by filtering out videos if desired, or simpler:
                    })}
              </h3>
            </div>
          </div>

          {/* Next Button */}
          <button
            onClick={handleNextImage}
            className="absolute right-4 sm:right-8 z-[10000] w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center text-xl transition-all border border-white/10"
            aria-label="Next image"
          >
            <FaChevronRight />
          </button>
        </div>
      )}
      <Footer />
    </div>
  );
}

export default GalleryPage;
